import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    FormControl, FormLabel, Typography, TextField, Autocomplete,
    Chip, Box, IconButton, LinearProgress, Divider,
    Checkbox, Button, Tooltip, Link, Dialog, DialogTitle, DialogContent, DialogActions,
    Popover,
} from '@mui/material';
import { Cancel, WhatsApp, Save, Add, Delete, ArrowBack, AttachFile, HighlightOff, GroupAdd, Phone, Edit as EditIcon, Person } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSelector, useDispatch } from 'react-redux';
import { selectActiveCustomers } from '../../store/customers/customersSlice';
import { SNACKBAR_OPEN } from '../../store/actions';
import JSDB from 'fromKotlin/nk';
import { dateObjectToString } from 'fromKotlin/nkutils';
import { getCofcLink } from '../../api/customer/documents.api';
import * as React from 'react';
import { completeWhatsAppSend } from '../../hooks/useWhatsAppSendLifecycle';
import { getWhatsappConfig, estimateQueuedBulkMinutes } from '../../hooks/useWhatsAppConfig';
import { fetchWhatsAppConnected } from '../../hooks/whatsappConnection';
import { buildCofcOrderShopLink, ORDER_SHOP_HOST } from '../../utils/whatsappShareUrl';
import Swal from 'sweetalert2';

export interface BulkSendPanelProps {
    baseUrl: string;
    token: string;
    sessionId: string;
    initialClients?: string[];
    initialMsg?: string;
    actionDate?: Date;
    externalVars?: string[];
    revision: number;
    onBack: () => void;
    onSendComplete?: () => void;
    /** Lifecycle token: success/failure calls completeWhatsAppSend here; onSendComplete only switches drawer mode */
    sendCompleteToken?: string;
}

interface Template { id: string; name: string; body?: string; content?: string; category?: string; }
interface Recipient { id: string; name: string; phone: string; externalVar?: string; }

/** Threshold: above this, recipient tags collapse to one "N לקוחות" chip (toggle to expand). */
const BULK_CHIPS_COLLAPSE_AT = 10;
const AUTOCOMPLETE_MAX_OPTIONS = 60;

const DAY_NAMES = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

interface ClientOption {
    id: string;
    name: string;
    phone: string;
    agent: string;
    category: string;
    category2: string;
    externalId: string;
    days: number[];
    label: string;
}

const TEMPLATE_CHIPS = [
    { label: '#שם', value: '#name' },
    { label: '#מזהה', value: '#id' },
    { label: '#איש קשר', value: '#contact' },
    { label: '#תאריך', value: '#date' },
    { label: '#יום', value: '#day' },
    { label: '#מזהה חיצוני', value: '#external_id' },
    { label: '#טלפון', value: '#phone' },
    { label: '#יתרה', value: '#var' },
    { label: '#קישור', value: '#link' },
    { label: '#קישור החזרות', value: '#link_returns' },
];

function buildNihulkavOrderLink(pass: string, dateStr: string, returns: boolean): string {
    const p = typeof pass === 'string' && pass.includes('&') ? pass.split('&')[0] : pass;
    return buildCofcOrderShopLink(p, dateStr, { returns });
}

function cleanPhone(raw: string): string {
    const digits = raw.replace(/[^0-9]/g, '');
    if (digits.startsWith('0') && digits.length === 10) return '972' + digits.slice(1);
    return digits;
}

function resolveVars(clientId: string): Record<string, string> {
    try {
        const client = JSDB().getClient(Number(clientId)).first;
        return {
            id: String(client.id || ''),
            external_id: client.getExternalId?.() || '',
            contact: client.getPhoneContact?.() || '',
        };
    } catch { return {}; }
}

const BulkSendPanel = ({
    baseUrl, token, sessionId, initialClients, initialMsg, actionDate, externalVars, revision, onBack, onSendComplete,
    sendCompleteToken,
}: BulkSendPanelProps) => {
    const sendCompleteTokenRef = React.useRef(sendCompleteToken);
    sendCompleteTokenRef.current = sendCompleteToken;
    const dispatch = useDispatch();
    const activeCustomers = useSelector(selectActiveCustomers);

    const clientOptions = useMemo((): ClientOption[] =>
        activeCustomers
            .filter((c) => c.phone)
            .map((c) => {
                let days: number[] = [];
                try {
                    const di = JSDB().getClient(Number(c.id)).first?.daysInt?.();
                    if (di && di.size > 0) days = di.toArray().slice() as number[];
                } catch {}
                return {
                    id: String(c.id),
                    name: c.name,
                    phone: c.phone,
                    agent: (c.agent || '').trim(),
                    category: (c.category || '').trim(),
                    category2: (c.group || '').trim(),
                    externalId: (c.externalId || '').trim(),
                    days,
                    label: `${c.name} - ${c.phone}`,
                };
            }),
        [activeCustomers]);

    const agentChoices = useMemo(
        () => Array.from(new Set(clientOptions.map((o) => o.agent).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'he')),
        [clientOptions],
    );
    const categoryChoices = useMemo(
        () => Array.from(new Set(clientOptions.map((o) => o.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'he')),
        [clientOptions],
    );
    const category2Choices = useMemo(
        () => Array.from(new Set(clientOptions.map((o) => o.category2).filter(Boolean))).sort((a, b) => a.localeCompare(b, 'he')),
        [clientOptions],
    );

    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [expandRecipientTags, setExpandRecipientTags] = useState(false);
    const [bulkAgent, setBulkAgent] = useState<string | null>(null);
    const [bulkCategory, setBulkCategory] = useState<string | null>(null);
    const [bulkCategory2, setBulkCategory2] = useState<string | null>(null);
    const [bulkDay, setBulkDay] = useState<number | null>(null);
    const [message, setMessage] = useState('');
    const [templates, setTemplates] = useState<Template[]>([]);
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<{ queued: number; failed: number; total: number } | null>(null);
    const [attachedFile, setAttachedFile] = useState<File | null>(null);
    const [queueNoticeOpen, setQueueNoticeOpen] = useState(false);
    const [openUserDetails, setOpenUserDetails] = useState(false);
    const [singleUser, setSingleUser] = useState({ id: '', name: '', phone: '', externalVar: '' });
    const [editPhone, setEditPhone] = useState(false);
    const [editExternalVar, setEditExternalVar] = useState(false);

    const waConfig = useMemo(() => getWhatsappConfig(), []);
    const isNewBulkForm = !!waConfig?.isNewApi;
    const queueNoticeMinutes = useMemo(() => {
        const n = recipients.length;
        const kind = attachedFile ? 'document' : 'message';
        return estimateQueuedBulkMinutes(n, kind);
    }, [recipients.length, attachedFile]);

    const headers = useMemo(() => ({
        'Content-Type': 'application/json', 'x-api-key': token,
    }), [token]);

    const loadTemplates = useCallback(async () => {
        try {
            const res = await fetch(`${baseUrl}/templates/list/${encodeURIComponent(sessionId)}`, { headers });
            const data = await res.json();
            if (data.success) setTemplates(data.data || []);
        } catch {}
    }, [baseUrl, sessionId, headers]);

    useEffect(() => { loadTemplates(); }, [loadTemplates]);

    useEffect(() => {
        if (revision <= 0) return;
        setResult(null);
        setLoading(false);

        if (initialClients?.length) {
            // Always use current phone for WhatsApp share — not the document/action date.
            const phoneDateStr = dateObjectToString(new Date());
            const recs = initialClients.map((id, idx) => {
                try {
                    const client = JSDB().getClient(Number(id)).first;
                    return {
                        id: String(id), name: client.name || '',
                        phone: client.getPhoneComplex?.(phoneDateStr, 0) || '',
                        externalVar: externalVars?.[idx] || '',
                    };
                } catch { return { id: String(id), name: '', phone: '', externalVar: externalVars?.[idx] || '' }; }
            }).filter(r => r.phone);
            setRecipients(recs);
            if (recs.length > BULK_CHIPS_COLLAPSE_AT) setExpandRecipientTags(false);
        }
        if (initialMsg) setMessage(initialMsg);
    }, [revision]);

    const resolveDate = useMemo(() => {
        const d = actionDate || new Date();
        return d.toLocaleDateString('he-IL');
    }, [actionDate]);

    const resolveDayName = useMemo(() => {
        const d = actionDate || new Date();
        const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
        return days[d.getDay()];
    }, [actionDate]);

    const saveTemplate = async () => {
        if (!templateName.trim() || !message.trim()) return;
        try {
            const res = await fetch(`${baseUrl}/templates/create/${encodeURIComponent(sessionId)}`, {
                method: 'POST', headers,
                body: JSON.stringify({ name: templateName.trim(), body: message }),
            });
            const data = await res.json();
            if (data.error) { console.error('Template save error:', data.message); return; }
            setTemplateName('');
            setSavingTemplate(false);
            loadTemplates();
        } catch (e) { console.error('Template save failed:', e); }
    };

    const deleteTemplate = async (id: string) => {
        try {
            await fetch(`${baseUrl}/templates/delete/${encodeURIComponent(sessionId)}/${id}`, {
                method: 'DELETE', headers,
            });
            loadTemplates();
        } catch {}
    };

    const performSend = async () => {
        if (!recipients.length || (!message.trim() && !attachedFile)) return;
        const waCfg = getWhatsappConfig();
        if (waCfg) {
            const ok = await fetchWhatsAppConnected(waCfg);
            if (!ok) {
                await Swal.fire({
                    title: 'WhatsApp לא מחובר',
                    html: '<p style="direction:rtl;text-align:right">אין חיבור ל-WhatsApp. התחבר דרך הסטטוס בראש המסך (צ׳יפ אדום) או סרוק QR.</p>',
                    icon: 'warning',
                    confirmButtonText: 'אישור',
                });
                return;
            }
        }
        setLoading(true);
        setResult(null);
        try {
            if (attachedFile) {
                let queued = 0, failed = 0;
                for (const r of recipients) {
                    try {
                        const jid = cleanPhone(r.phone) + '@s.whatsapp.net';
                        const fd = new FormData();
                        fd.append('file', attachedFile);
                        const resolvedMsg = message ? resolvePreview(message, r) : '';
                        fd.append('caption', resolvedMsg);
                        await fetch(
                            `${baseUrl}/chat/upload/${encodeURIComponent(sessionId)}/${encodeURIComponent(jid)}?access_token=${encodeURIComponent(token)}`,
                            { method: 'POST', body: fd },
                        );
                        queued++;
                    } catch { failed++; }
                }
                dispatch({
                    type: SNACKBAR_OPEN, open: true,
                    message: `נשלחו ${queued} מתוך ${recipients.length} קבצים בהצלחה`,
                    variant: 'alert', alertSeverity: failed === 0 ? 'success' : 'warning',
                });
                setTimeout(() => {
                    completeWhatsAppSend(sendCompleteTokenRef.current, true, 'bulk');
                    onSendComplete?.();
                }, 600);
            } else {
                const needsLink = message.includes('#link');
                const needsLinkReturns = message.includes('#link_returns');
                const needsAnyOrderLink = needsLink || needsLinkReturns;
                const dateStr = dateObjectToString(actionDate || new Date());
                const fallbackShop = (id: string) => (id ? `${ORDER_SHOP_HOST}/?client_id=${encodeURIComponent(id)}` : ORDER_SHOP_HOST);
                const recipientsWithLinks = await Promise.all(
                    recipients.map(async (r) => {
                        let link = '';
                        let linkReturns = '';
                        if (needsAnyOrderLink && r.id) {
                            try {
                                const raw = await getCofcLink(Number(r.id), actionDate || new Date());
                                const pass = typeof raw === 'string' && raw.includes('&') ? raw.split('&')[0] : String(raw);
                                if (needsLink) link = buildNihulkavOrderLink(pass, dateStr, false);
                                if (needsLinkReturns) linkReturns = buildNihulkavOrderLink(pass, dateStr, true);
                            } catch {
                                const fb = fallbackShop(r.id);
                                if (needsLink) link = fb;
                                if (needsLinkReturns) linkReturns = fb;
                            }
                        }
                        return { ...r, cofcLink: link, cofcLinkReturns: linkReturns };
                    })
                );
                const payload = {
                    template: message,
                    recipients: recipientsWithLinks.map((r) => ({
                        phone: cleanPhone(r.phone), name: r.name, client_id: r.id,
                        vars: {
                            ...resolveVars(r.id),
                            date: resolveDate,
                            day: resolveDayName,
                            var: r.externalVar || '',
                            link: r.cofcLink || fallbackShop(r.id),
                            link_returns: r.cofcLinkReturns || fallbackShop(r.id),
                        },
                    })),
                };
                const res = await fetch(
                    `${baseUrl}/bulk/send/${encodeURIComponent(sessionId)}`,
                    { method: 'POST', headers, body: JSON.stringify(payload) },
                );
                const data = await res.json();
                if (data.success && data.data) {
                    const r = data.data;
                    dispatch({
                        type: SNACKBAR_OPEN, open: true,
                        message: `נשלחו ${r.queued} מתוך ${r.total} הודעות בהצלחה`,
                        variant: 'alert', alertSeverity: r.failed === 0 ? 'success' : 'warning',
                    });
                    setTimeout(() => {
                        completeWhatsAppSend(sendCompleteTokenRef.current, true, 'bulk');
                        onSendComplete?.();
                    }, 600);
                } else {
                    completeWhatsAppSend(sendCompleteTokenRef.current, false, 'bulk');
                }
            }
        } catch {
            completeWhatsAppSend(sendCompleteTokenRef.current, false, 'bulk');
            dispatch({
                type: SNACKBAR_OPEN, open: true, message: 'שליחה נכשלה', variant: 'alert', alertSeverity: 'error',
            });
        }
        setLoading(false);
    };

    const requestSend = () => {
        if (!recipients.length || (!message.trim() && !attachedFile) || loading) return;
        if (isNewBulkForm) setQueueNoticeOpen(true);
        else void performSend();
    };

    const confirmQueuedSend = () => {
        setQueueNoticeOpen(false);
        void performSend();
    };

    const resolvePreview = (tpl: string, r: Recipient, cofcLink?: string, cofcLinkReturns?: string) => {
        let t = tpl;
        const vars = resolveVars(r.id);
        const dashboardBase = ORDER_SHOP_HOST;
        const link = cofcLink || (r.id ? `${dashboardBase}/?client_id=${encodeURIComponent(r.id)}` : dashboardBase);
        const linkRet = cofcLinkReturns || link;
        t = t.replace(/#name/g, r.name).replace(/#phone/g, r.phone);
        t = t.replace(/#date/g, resolveDate).replace(/#day/g, resolveDayName);
        t = t.replace(/#var/g, r.externalVar || '');
        t = t.replace(/#link_returns/g, linkRet);
        t = t.replace(/#link/g, link);
        for (const [k, v] of Object.entries(vars)) t = t.replace(new RegExp(`#${k}`, 'g'), v);
        return t;
    };

    const noPhone = recipients.some(r => !r.phone);

    const selectedClientOptions = useMemo(
        () =>
            recipients.map((r) => {
                const found = clientOptions.find((o) => o.id === r.id);
                const base = found ?? {
                    id: r.id,
                    name: r.name,
                    phone: r.phone,
                    agent: '',
                    category: '',
                    category2: '',
                    externalId: '',
                    days: [] as number[],
                    label: `${r.name} - ${r.phone}`,
                };
                return {
                    ...base,
                    name: r.name,
                    phone: r.phone,
                    label: `${r.name} - ${r.phone}`,
                } as ClientOption;
            }),
        [recipients, clientOptions],
    );

    const updateRecipientField = useCallback(
        (id: string, patch: Partial<Pick<Recipient, 'phone' | 'externalVar'>>) => {
            setRecipients((prev) =>
                prev.map((row) => (row.id === id ? { ...row, ...patch } : row)),
            );
        },
        [],
    );

    const addClientsByFilters = useCallback(() => {
        if (!bulkAgent && !bulkCategory && !bulkCategory2 && bulkDay == null) {
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'בחר סוכן, קטגוריה, קטגוריה 2 או יום לפני הוספה',
                variant: 'alert',
                alertSeverity: 'warning',
            });
            return;
        }
        const matches = clientOptions.filter((o) => {
            if (bulkAgent && o.agent !== bulkAgent) return false;
            if (bulkCategory && o.category !== bulkCategory) return false;
            if (bulkCategory2 && o.category2 !== bulkCategory2) return false;
            if (bulkDay != null && !o.days.includes(bulkDay)) return false;
            return true;
        });
        if (matches.length === 0) {
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'לא נמצאו לקוחות תואמים לסינון',
                variant: 'alert',
                alertSeverity: 'info',
            });
            return;
        }
        setRecipients((prev) => {
            const byId = new Map(prev.map((r) => [r.id, r]));
            for (const m of matches) {
                if (!byId.has(m.id)) byId.set(m.id, { id: m.id, name: m.name, phone: m.phone });
            }
            return Array.from(byId.values());
        });
        if (matches.length > BULK_CHIPS_COLLAPSE_AT) setExpandRecipientTags(false);
    }, [bulkAgent, bulkCategory, bulkCategory2, bulkDay, clientOptions, dispatch]);

    const previewText = useMemo(() => {
        if (!recipients.length || !message.trim()) return '';
        let preview = message;
        const r = recipients[0];
        const vars = resolveVars(r.id);
        const dashboardBase = ORDER_SHOP_HOST;
        const previewLink = r.id ? `${dashboardBase}/?client_id=${encodeURIComponent(r.id)}` : dashboardBase;
        preview = preview.replace(/#name/g, r.name);
        preview = preview.replace(/#phone/g, r.phone);
        preview = preview.replace(/#date/g, resolveDate);
        preview = preview.replace(/#day/g, resolveDayName);
        preview = preview.replace(/#var/g, r.externalVar || '');
        preview = preview.replace(/#link_returns/g, `${previewLink} (החזרות)`);
        preview = preview.replace(/#link/g, previewLink);
        for (const [k, v] of Object.entries(vars)) {
            preview = preview.replace(new RegExp(`#${k}`, 'g'), v);
        }
        return preview;
    }, [recipients, message, resolveDate, resolveDayName]);

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 1.5, py: 1, backgroundColor: '#f0f2f5', borderBottom: '1px solid #e5e7eb',
            }}>
                <IconButton size="small" onClick={onBack}><ArrowBack fontSize="small" /></IconButton>
                <WhatsApp color="success" sx={{ fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight={600}>שליחה מרובה</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {recipients.length} נמענים
                </Typography>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FormControl fullWidth size="small">
                    <FormLabel sx={{ mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>נמענים</Typography>
                    </FormLabel>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        חיפוש לפי שם, טלפון, סוכן, קטגוריות או מזהה חיצוני · חיצים לניווט, Enter להוספה מהירה · סימון בתיבות
                    </Typography>
                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        size="small"
                        options={clientOptions}
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        value={selectedClientOptions}
                        onChange={(_, val: ClientOption[]) => {
                            setRecipients((prev) => {
                                const prevById = new Map(prev.map((r) => [r.id, r]));
                                return val.map((v) => {
                                    const existing = prevById.get(v.id);
                                    if (existing) {
                                        return existing;
                                    }
                                    return { id: v.id, name: v.name, phone: v.phone };
                                });
                            });
                        }}
                        filterOptions={(options, { inputValue }) => {
                            const q = inputValue.toLowerCase().trim();
                            if (!q) return options.slice(0, AUTOCOMPLETE_MAX_OPTIONS);
                            return options
                                .filter(
                                    (o) =>
                                        o.name.toLowerCase().includes(q) ||
                                        o.phone.includes(q) ||
                                        o.agent.toLowerCase().includes(q) ||
                                        o.category.toLowerCase().includes(q) ||
                                        o.category2.toLowerCase().includes(q) ||
                                        o.externalId.toLowerCase().includes(q),
                                )
                                .slice(0, AUTOCOMPLETE_MAX_OPTIONS);
                        }}
                        renderOption={(props, option, state) => {
                            const { key, ...liProps } = props as { key?: string } & React.HTMLAttributes<HTMLLIElement>;
                            return (
                                <li {...liProps} key={key ?? option.id} style={{ direction: 'rtl', display: 'flex', alignItems: 'center' }}>
                                    <Checkbox size="small" checked={state.selected} sx={{ py: 0, mr: 0.5 }} tabIndex={-1} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" noWrap>{option.name}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {option.phone}
                                            {option.agent ? ` · ${option.agent}` : ''}
                                            {option.category ? ` · ${option.category}` : ''}
                                        </Typography>
                                    </Box>
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder="חפש לקוח..."
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.stopPropagation();
                                }}
                            />
                        )}
                        renderTags={(value, getTagProps) => {
                            if (value.length > BULK_CHIPS_COLLAPSE_AT && !expandRecipientTags) {
                                const tip =
                                    recipients
                                        .map((r) => r.name)
                                        .slice(0, 40)
                                        .join(', ') + (recipients.length > 40 ? '…' : '');
                                return [
                                    <Tooltip key="bulk-sum" title={tip} placement="top" enterDelay={400}>
                                        <Chip
                                            label={`${value.length} לקוחות`}
                                            size="small"
                                            onDelete={() => setRecipients([])}
                                            sx={{ backgroundColor: '#e3f2fd', fontWeight: 700 }}
                                        />
                                    </Tooltip>,
                                ];
                            }
                            return value.map((opt: ClientOption, idx) => {
                                const r = recipients.find((x) => x.id === opt.id);
                                return (
                                    <span key={opt.id}>
                                        <Chip
                                            variant="outlined"
                                            label={(
                                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.75, alignItems: 'center' }}>
                                                    <Typography component="span" variant="body2">{r?.name ?? opt.name}</Typography>
                                                    <Phone
                                                        sx={{
                                                            color: !r?.phone || r.phone === '' ? 'error.main' : 'text.secondary',
                                                            fontSize: 16,
                                                        }}
                                                    />
                                                </Box>
                                            )}
                                            id={`bulk-send-user-details-${idx}`}
                                            {...getTagProps({ index: idx })}
                                            onDelete={getTagProps({ index: idx }).onDelete}
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={() => {
                                                setSingleUser({
                                                    id: String(r?.id ?? opt.id),
                                                    name: r?.name ?? opt.name,
                                                    phone: r?.phone ?? '',
                                                    externalVar: r?.externalVar ?? '',
                                                });
                                                setEditPhone(false);
                                                setEditExternalVar(false);
                                                setOpenUserDetails(true);
                                            }}
                                            size="small"
                                            sx={{
                                                backgroundColor: !r?.phone ? '#ff4e007d' : '#ede7f6',
                                                fontWeight: !r?.phone ? 700 : 400,
                                            }}
                                        />
                                        <Popover
                                            open={openUserDetails && String(singleUser.id) === String(opt.id)}
                                            anchorEl={() => document.getElementById(`bulk-send-user-details-${idx}`) as HTMLElement}
                                            onClose={() => {
                                                setOpenUserDetails(false);
                                                setEditPhone(false);
                                                setEditExternalVar(false);
                                            }}
                                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                            /* Autocomplete steals focus / swallows keys unless events don't bubble to it */
                                            PaperProps={{
                                                onMouseDown: (e) => e.stopPropagation(),
                                            }}
                                        >
                                            <Box
                                                sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2, minWidth: 260 }}
                                                onMouseDown={(e) => e.stopPropagation()}
                                            >
                                                <Typography sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                                                    <Person fontSize="small" /> {singleUser.name}
                                                </Typography>
                                                {editPhone ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                                                        <TextField
                                                            label="טלפון"
                                                            size="small"
                                                            fullWidth
                                                            value={singleUser.phone}
                                                            id="bulk-send-user-details-phone-input"
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setSingleUser((s) => ({ ...s, phone: v }));
                                                                updateRecipientField(String(opt.id), { phone: v });
                                                            }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                            onClick={() => {
                                                                setTimeout(() => {
                                                                    document.getElementById('bulk-send-user-details-phone-input')?.focus();
                                                                }, 100);
                                                            }}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => setEditPhone(false)}
                                                            aria-label="שמור טלפון"
                                                        >
                                                            <Save fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Typography sx={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        gap: 1,
                                                        alignItems: 'center',
                                                        fontWeight: !singleUser.phone ? 700 : 400,
                                                    }}>
                                                        <Phone color={!singleUser.phone ? 'error' : 'inherit'} />
                                                        {singleUser.phone || 'לא קיים'}
                                                        <EditIcon
                                                            sx={{ cursor: 'pointer', fontSize: 18 }}
                                                            onClick={() => {
                                                                setEditPhone(true);
                                                                setTimeout(() => {
                                                                    document.getElementById('bulk-send-user-details-phone-input')?.focus();
                                                                }, 100);
                                                            }}
                                                        />
                                                    </Typography>
                                                )}
                                                {editExternalVar ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'flex-start' }}>
                                                        <TextField
                                                            label="משתנה (#var / יתרה)"
                                                            size="small"
                                                            fullWidth
                                                            multiline
                                                            minRows={2}
                                                            value={singleUser.externalVar}
                                                            id="bulk-send-user-details-var-input"
                                                            onChange={(e) => {
                                                                const v = e.target.value;
                                                                setSingleUser((s) => ({ ...s, externalVar: v }));
                                                                updateRecipientField(String(opt.id), { externalVar: v });
                                                            }}
                                                            onMouseDown={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => e.stopPropagation()}
                                                        />
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            sx={{ mt: 0.5 }}
                                                            onClick={() => setEditExternalVar(false)}
                                                            aria-label="שמור משתנה"
                                                        >
                                                            <Save fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <span style={{ color: 'var(--mui-palette-text-secondary)' }}>#var:</span>
                                                        {singleUser.externalVar || '—'}
                                                        <EditIcon
                                                            sx={{ cursor: 'pointer', fontSize: 18, ml: 0.5 }}
                                                            onClick={() => {
                                                                setEditExternalVar(true);
                                                                setTimeout(() => {
                                                                    document.getElementById('bulk-send-user-details-var-input')?.focus();
                                                                }, 100);
                                                            }}
                                                        />
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Popover>
                                    </span>
                                );
                            });
                        }}
                        sx={{ maxHeight: 140, overflowY: 'auto' }}
                    />
                    {recipients.length > BULK_CHIPS_COLLAPSE_AT && (
                        <Typography
                            component="button"
                            variant="caption"
                            onClick={() => setExpandRecipientTags((x) => !x)}
                            sx={{
                                mt: 0.5,
                                cursor: 'pointer',
                                border: 0,
                                background: 'none',
                                color: 'primary.main',
                                textDecoration: 'underline',
                                display: 'block',
                            }}
                        >
                            {expandRecipientTags ? 'הצג כקבוצה אחת (קומפקטי)' : `הצג את כל ${recipients.length} השמות לעריכה`}
                        </Typography>
                    )}
                </FormControl>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight={600}>הוספת קבוצה</Typography>
                    <Typography variant="caption" color="text.secondary">
                        בחר סוכן ו/או קטגוריה ו/או קטגוריה 2 — כל השדות שנבחרו חייבים להתאים (AND). לחיצה מוסיפה את כל הלקוחות עם טלפון.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                        <Autocomplete
                            size="small"
                            sx={{ minWidth: 160, flex: '1 1 140px' }}
                            options={agentChoices}
                            value={bulkAgent}
                            onChange={(_, v) => setBulkAgent(v)}
                            renderInput={(p) => <TextField {...p} label="סוכן" placeholder="הכל" />}
                        />
                        <Autocomplete
                            size="small"
                            sx={{ minWidth: 160, flex: '1 1 140px' }}
                            options={categoryChoices}
                            value={bulkCategory}
                            onChange={(_, v) => setBulkCategory(v)}
                            renderInput={(p) => <TextField {...p} label="קטגוריה" placeholder="הכל" />}
                        />
                        <Autocomplete
                            size="small"
                            sx={{ minWidth: 160, flex: '1 1 140px' }}
                            options={category2Choices}
                            value={bulkCategory2}
                            onChange={(_, v) => setBulkCategory2(v)}
                            renderInput={(p) => <TextField {...p} label="קטגוריה 2" placeholder="הכל" />}
                        />
                        <Autocomplete
                            size="small"
                            sx={{ minWidth: 130, flex: '1 1 100px' }}
                            options={DAY_NAMES.map((name, i) => ({ label: name, value: i + 1 }))}
                            getOptionLabel={(o) => o.label}
                            value={bulkDay != null ? { label: DAY_NAMES[bulkDay - 1] || '', value: bulkDay } : null}
                            onChange={(_, v) => setBulkDay(v?.value ?? null)}
                            isOptionEqualToValue={(a, b) => a.value === b.value}
                            renderInput={(p) => <TextField {...p} label="יום חלוקה" placeholder="הכל" />}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<GroupAdd />}
                            onClick={addClientsByFilters}
                            sx={{ textTransform: 'none' }}
                        >
                            הוסף לקוחות מהסינון
                        </Button>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>תבניות שמורות</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {templates.map((t) => (
                            <Chip key={t.id} label={t.name} variant="outlined" size="small"
                                onClick={() => setMessage(t.body || t.content || '')}
                                onDelete={() => deleteTemplate(t.id)}
                                deleteIcon={<Delete sx={{ fontSize: 14 }} />}
                            />
                        ))}
                        {savingTemplate ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TextField size="small" placeholder="שם תבנית" value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)} sx={{ width: 120 }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') saveTemplate(); }}
                                />
                                <IconButton size="small" color="primary" onClick={saveTemplate}><Save fontSize="small" /></IconButton>
                                <IconButton size="small" color="error" onClick={() => setSavingTemplate(false)}><Cancel fontSize="small" /></IconButton>
                            </Box>
                        ) : (
                            <Chip label="שמור תבנית" size="small" variant="outlined"
                                icon={<Add sx={{ fontSize: 14 }} />}
                                onClick={() => setSavingTemplate(true)} disabled={!message.trim()}
                            />
                        )}
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>משתנים</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {TEMPLATE_CHIPS.map((tc) => (
                            <Chip key={tc.value} label={tc.label} size="small" variant="outlined"
                                onClick={() => setMessage((prev) => prev + ' ' + tc.value + ' ')}
                            />
                        ))}
                    </Box>
                </Box>

                <FormControl fullWidth size="small">
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>הודעה</Typography>
                    <TextField multiline rows={3} variant="outlined"
                        placeholder="הקלד הודעה עם משתנים כמו #name #date ..."
                        value={message} onChange={(e) => setMessage(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { fontSize: 13 } }}
                    />
                </FormControl>

                {/* File attachment */}
                <Box>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>צרף קובץ</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input type="file" id="bulk-file-input" hidden
                            onChange={(e) => { if (e.target.files?.[0]) setAttachedFile(e.target.files[0]); }}
                        />
                        <Chip
                            icon={<AttachFile sx={{ fontSize: 16 }} />}
                            label={attachedFile ? attachedFile.name : 'בחר קובץ'}
                            size="small"
                            variant={attachedFile ? 'filled' : 'outlined'}
                            color={attachedFile ? 'primary' : 'default'}
                            onClick={() => document.getElementById('bulk-file-input')?.click()}
                            sx={{ cursor: 'pointer' }}
                        />
                        {attachedFile && (
                            <IconButton size="small" color="error" onClick={() => setAttachedFile(null)}>
                                <HighlightOff sx={{ fontSize: 18 }} />
                            </IconButton>
                        )}
                    </Box>
                </Box>

                {previewText && (
                    <Box>
                        <Divider sx={{ mb: 0.5 }} />
                        <Typography variant="caption" color="text.secondary">
                            תצוגה מקדימה ({recipients[0]?.name}):
                        </Typography>
                        <Box sx={{
                            p: 1, mt: 0.5, backgroundColor: '#dcfce7', borderRadius: 2,
                            fontSize: 12, whiteSpace: 'pre-wrap', direction: 'rtl', lineHeight: 1.5,
                        }}>
                            {previewText}
                        </Box>
                    </Box>
                )}

                {loading && <LinearProgress />}
            </Box>

            <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e5e7eb', backgroundColor: '#f0f2f5' }}>
                <LoadingButton variant="contained" fullWidth loading={loading}
                    color={noPhone ? 'error' : 'success'}
                    disabled={!recipients.length || (!message.trim() && !attachedFile) || loading}
                    onClick={requestSend} startIcon={<WhatsApp />}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                    {noPhone ? 'לחלק מהנמענים אין טלפון' : attachedFile ? `שלח קובץ ל-${recipients.length} נמענים` : `שלח ל-${recipients.length} נמענים`}
                </LoadingButton>
            </Box>

            <Dialog open={queueNoticeOpen} onClose={() => !loading && setQueueNoticeOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>שליחה בתור (ווטסאפ)</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
                        ההודעות נשלחות בהדרגה — לא מיד. לפי מספר הנמענים ({recipients.length}), זמן משוער: בערך{' '}
                        <strong>{queueNoticeMinutes} דקות</strong>.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" component="div" sx={{ mb: 1 }}>
                        הערכת קצב: כ-50 מסמכים ≈ 12 דקות, כ-50 הודעות טקסט בלבד ≈ 8 דקות.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        אפשר לעקוב אחרי ההתקדמות בחלון הווטסאפ.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQueueNoticeOpen(false)} disabled={loading}>ביטול</Button>
                    <Button variant="contained" onClick={confirmQueuedSend} disabled={loading} color="success">
                        המשך לשליחה
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BulkSendPanel;
